import { useState, useCallback, useMemo } from "react";
import { BASE_URL } from "../../apiConfig";
import { useAuth } from "../../context/AuthProvider";
import { toast } from 'sonner'; 
import {Link} from "react-router-dom";
import { useLanguage } from "../../context/LanguageProvider";

export default function CreateFournisseurForm() {
  const { t } = useLanguage();
  const { token } = useAuth();
  const [formData, setFormData] = useState({
    name: "",          
    name_personnel: "", 
    email: "",         
    phone: "",         
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
    setSuccess(false);
    setErrorMessage("");
  }, [errors]);

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('fournisseurs.validation.name_required');
    }
    if (!formData.name_personnel.trim()) {
      newErrors.name_personnel = t('fournisseurs.validation.personnel_required');
    }
    if (!formData.email.trim()) {
      newErrors.email = t('fournisseurs.validation.email_required');
    } else if (!/\S+.\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('fournisseurs.validation.email_valid');
    }
    if (!formData.phone.trim()) {
      newErrors.phone = t('fournisseurs.validation.phone_required');
    } else if (!/^\+?[0-9\s-()]{7,20}$/.test(formData.phone)) {
      newErrors.phone = t('fournisseurs.validation.phone_valid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (!validateForm()) {
      toast.error(t('fournisseurs.errors.form_errors'));
      return;
    }

    setIsLoading(true); 

    try {
      const response = await fetch(BASE_URL + "/api/fournisseurs", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json", 
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (!response.ok) {
        if (response.status === 422 && result.errors) {
          setErrors(result.errors);
          toast.error(t('fournisseurs.errors.form_errors'));
        } else {
          const msg = result.message || t('fournisseurs.errors.create_failed');
          setErrorMessage(msg);
          toast.error(msg);
        }
        return;
      }

      setSuccess(true);
      toast.success(t('fournisseurs.success_created'));
      setFormData({ name: "", name_personnel: "", email: "", phone: "" });
      setErrors({});
      setTimeout(() => setSuccess(false), 3000);

    } catch (error) {
      console.error("Error creating fournisseur:", error);
      setErrorMessage(t('fournisseurs.errors.unexpected'));
      toast.error(t('fournisseurs.errors.unexpected_short'));
    } finally {
      setIsLoading(false);
    }
  };

  const previewData = useMemo(() => {
    return {
      displayName: formData.name || t('fournisseurs.supplier_name_default'),
      displayPersonnel: formData.name_personnel || t('fournisseurs.contact_person_default'),
      displayEmail: formData.email || t('fournisseurs.email_default'),
      displayPhone: formData.phone || t('fournisseurs.phone_default'),
    };
  }, [formData]);

  return (
    <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
      {success && (
        <div className="bg-[var(--color-primary-500)] text-white p-6 text-center">
          <div className="flex items-center justify-center mb-2">
            <svg className="h-8 w-8 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-lg font-semibold">{t('fournisseurs.success_created')}</span>
          </div>
          <p className="text-[var(--color-primary-50)] text-xs">{t('fournisseurs.success_description')}</p>
        </div>
      )}

      {errorMessage && (
        <div className="bg-red-500 text-xs text-white p-4 text-center">
          <p>{errorMessage}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="p-8">
        <div className="mb-8">
          <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-3">{t('forms.labels.supplier_name')}</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder={t('fournisseurs.supplier_name_placeholder')}
            className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
              errors.name
                ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
            }`}
          />
          {errors.name && <p className="mt-2 text-red-600 text-xs">{errors.name}</p>}
        </div>

        <div className="mb-8">
          <label htmlFor="name_personnel" className="block text-sm font-semibold text-gray-900 mb-3">{t('forms.labels.contact_name')}</label>
          <input
            type="text"
            id="name_personnel"
            name="name_personnel"
            value={formData.name_personnel}
            onChange={handleInputChange}
            placeholder={t('fournisseurs.contact_person_placeholder')}
            className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
              errors.name_personnel
                ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
            }`}
          />
          {errors.name_personnel && <p className="mt-2 text-red-600 text-xs">{errors.name_personnel}</p>}
        </div>

        <div className="mb-8">
          <label htmlFor="email" className="block font-semibold text-sm text-gray-900 mb-3">{t('forms.labels.email')}</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder={t('fournisseurs.email_placeholder')}
            className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
              errors.email
                ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
            }`}
          />
          {errors.email && <p className="mt-2 text-red-600 text-xs">{errors.email}</p>}
        </div>

        <div className="mb-8">
          <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-3">{t('forms.labels.phone')}</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            placeholder={t('fournisseurs.phone_placeholder')}
            className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
              errors.phone
                ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
            }`}
          />
          {errors.phone && <p className="mt-2 text-red-600 text-xs">{errors.phone}</p>}
        </div>

        <div className="flex sm:flex-row gap-4 pt-6 border-t border-gray-200">
          <Link to="/fournisseurs" className="block cursor-pointer text-center flex-1 py-3 px-4 cursor-pointer text-sm border-2 border-gray-200 text-gray-700 font-semibold rounded-2xl hover:border-gray-300 hover:bg-gray-50 transition-all duration-200">
            <button
              type="button"
              disabled={isLoading}
              className='cursor-pointer'
            >
              {t('buttons.cancel')}
            </button>
          </Link>  
          <button
            type="submit"
            disabled={isLoading}
            className={`flex-1 py-3 cursor-pointer px-4 text-sm font-semibold rounded-2xl ${
              isLoading
                ? "bg-gray-400 text-white cursor-not-allowed"
                : "bg-[var(--color-primary-500)] hover:bg-[var(--color-primary-400)] text-white shadow-lg hover:shadow-xl transform hover:scale-105"
            }`}
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {t('fournisseurs.saving')}
              </span>
            ) : (
              <span className="flex items-center justify-center">
                {t('buttons.save')}
                <svg className="h-4 w-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </span>
            )}
          </button>
        </div>

        {(formData.name || formData.name_personnel || formData.email || formData.phone) && (
          <div className="mt-8 p-6 bg-gray-50 rounded-2xl">
            <h3 className="font-semibold text-gray-900 mb-4">{t('fournisseurs.live_preview')}</h3>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[var(--color-primary-50)] rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="h-6 w-6 text-[var(--color-primary-500)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21v-2a4 4 0 00-4-4H9a4 4 0 00-4 4v2m7-12a4 4 0 100-8 4 4 0 000 8zm7 0a4 4 0 100-8 4 4 0 000 8zm-7 0a4 4 0 100-8 4 4 0 000 8z"
                  />
                </svg>
              </div>
              <div>
                <div className="font-semibold text-sm text-gray-900">{previewData.displayName}</div>
                <div className="text-xs text-gray-600 mt-1">
                  {t('fournisseurs.contact')} {previewData.displayPersonnel}
                </div>
                <div className="text-xs text-gray-600">
                  {t('fournisseurs.email_display')} {previewData.displayEmail}
                </div>
                <div className="text-xs text-gray-600">
                  {t('fournisseurs.phone_display')} {previewData.displayPhone}
                </div>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
